// This file has been fixed by Gemini to resolve a syntax error.
import React from 'react';
import { Recommendation } from '../types';
import { 
  formatCurrency, 
  formatDate, 
  formatPercentage, 
  getRecommendationBadgeClass,
  getUpsideColor,
  getStockScoreBadgeClass
} from '../utils';
import { ExternalLink, TrendingUp, TrendingDown } from 'lucide-react';

interface RecommendationCardProps {
  recommendation: Recommendation;
  onClick?: (recommendation: Recommendation) => void;
}

export const RecommendationCard: React.FC<RecommendationCardProps> = ({ 
  recommendation, 
  onClick 
}) => {
  const handleClick = () => {
    if (onClick) {
      onClick(recommendation);
    }
  };

  // Helper functions to extract data from different field formats
  const getStockName = () => {
    return recommendation.company?.name || recommendation.stock?.name || 'Unknown Stock';
  };

  const getStockSymbol = () => {
    return recommendation.company?.seo_name || recommendation.stock?.symbol || 'N/A';
  };

  const getSector = () => {
    return recommendation.company?.sector || recommendation.sector || recommendation.stock?.sector || null;
  };

  const getRecommendationType = () => {
    return recommendation.recommendation_type || recommendation.recommendation || 'N/A';
  };

  const getTargetPrice = () => {
    return parseFloat(String(recommendation.target_price || recommendation.targetPrice || 0));
  };

  const getCurrentPrice = () => {
    return parseFloat(String(recommendation.current_price || recommendation.stock?.current_price || 0));
  };

  const getPriceAtReco = () => {
    return parseFloat(String(recommendation.current_price || recommendation.priceAtReco || 0));
  };

  const getUpside = () => {
    return parseFloat(String(recommendation.upside_potential || recommendation.potentialUpside || 0));
  };

  const getBrokerageName = () => {
    return recommendation.organization?.name || recommendation.brokerage?.name || 'Unknown Brokerage';
  };

  const getDate = () => {
    return recommendation.recommendation_date || recommendation.call_date || recommendation.created_at;
  };

  const getReportUrl = () => {
    return recommendation.pdf_url || recommendation.reportUrl;
  };

  const getStockScore = () => {
    const score = (recommendation as any).stock_score;
    if (score === null || score === undefined || score === '') return null;
    const numScore = typeof score === 'string' ? parseFloat(score) : score;
    return isNaN(numScore) ? null : numScore;
  };

  const upsideValue = getUpside();
  const isDownside = recommendation.potentialDownside && recommendation.potentialDownside > 0;
  const displayValue = isDownside ? (recommendation.potentialDownside || 0) : upsideValue;
  const isPositive = displayValue > 0;

  const getRecommendationStyle = (type: string) => {
    const upperType = type.toUpperCase();
    if (upperType.includes('BUY')) {
      return 'bg-gradient-to-r from-green-500 to-green-600 text-white';
    } else if (upperType.includes('SELL')) {
      return 'bg-gradient-to-r from-red-500 to-rose-600 text-white';
    } else if (upperType.includes('HOLD') || upperType.includes('NEUTRAL')) {
      return 'bg-gradient-to-r from-yellow-500 to-amber-600 text-white';
    }
    return 'bg-gradient-to-r from-gray-500 to-gray-600 text-white';
  };

  return (
    <div 
      className="bg-obsidian rounded-xl shadow-sm border border-white/20 p-5 hover:shadow-md transition-all duration-200 cursor-pointer"
      onClick={handleClick}
    >
      {/* Header with Stock Name and Recommendation Badge */}
      <div className="flex justify-between items-start mb-3 gap-3">
        <div className="flex-1 min-w-0">
          <h3 className="font-bold text-base text-white mb-0.5 truncate">
            {getStockName()}
          </h3>
          <p className="text-xs font-medium text-gray-500 truncate">{getStockSymbol()}</p>
        </div>
        <span className={`px-2.5 py-1 rounded-md text-xs font-bold whitespace-nowrap flex-shrink-0 ${getRecommendationStyle(getRecommendationType())}`}>
          {getRecommendationType()}
        </span>
      </div>

      {/* Sector Badge and Stock Score */}
      <div className="mb-3 flex items-center gap-2 flex-wrap">
        {getSector() && (
          <span className="inline-block text-xs px-2.5 py-0.5 bg-blue-50 text-blue-700 rounded">
            {getSector()}
          </span>
        )}
        {getStockScore() !== null && (
          <span className={`inline-block text-xs font-bold ${getStockScoreBadgeClass(getStockScore())}`}>
            Score: {getStockScore()?.toFixed(1)}
          </span>
        )}
      </div>

      {/* Price Info Grid */}
      <div className="grid grid-cols-2 gap-3 mb-3">
        <div>
          <div className="text-xs text-gray-500 mb-0.5">Target</div>
          <div className="font-bold text-sm text-white">₹{getTargetPrice().toLocaleString('en-IN', { maximumFractionDigits: 2 })}</div>
        </div>
        <div>
          <div className="text-xs text-gray-500 mb-0.5">Current</div>
          <div className="font-bold text-sm text-white">₹{getCurrentPrice().toLocaleString('en-IN', { maximumFractionDigits: 2 })}</div>
        </div>
      </div>

      {/* Footer with Brokerage and Upside */}
      <div className="pt-3 border-t border-gray-100">
        <div className="flex items-center justify-between gap-3">
          <div className="flex items-center gap-2 min-w-0 flex-1">
            <div className="w-8 h-8 rounded-full bg-blue-600 flex items-center justify-center flex-shrink-0">
              <span className="text-white text-xs font-bold">
                {getBrokerageName().substring(0, 2).toUpperCase()}
              </span>
            </div>
            <div className="min-w-0 flex-1">
              <p className="font-semibold text-xs text-white truncate">
                {getBrokerageName()}
              </p>
              <p className="text-xs text-gray-500">
                {formatDate(getDate())}
              </p>
            </div>
          </div>
          
          <div className={`flex items-center gap-1 ${isPositive ? 'text-red-600' : 'text-green-600'}`}>
            {isPositive ? <TrendingDown className="h-4 w-4" /> : <TrendingUp className="h-4 w-4" />}
            <span className="font-bold text-sm whitespace-nowrap">
              {isPositive ? '' : '+'}{displayValue.toFixed(2)}%
            </span>
          </div>
        </div>
      </div>
    </div>
  );
};